/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, ComponentRef, Directive, ElementRef, Injector, OnDestroy, OutputRefSubscription, Renderer2, ViewContainerRef, afterNextRender, computed, effect, inject, input, signal } from '@angular/core';
import { HisHelptextButtonComponent } from '../../structure/component/helptext/his-helptext-button.component';
import { HisHelptextContentComponent } from '../../structure/component/helptext/his-helptext-content.component';
import { HisUiService } from '../service/base/his-ui.service';

/**
 * Directive für p-panel: Fügt einen Hilfetext-Button rechts im Panel-Header ein, wenn ein i18n-Schlüssel
 * übergeben wird.
 *
 * Der Button blendet einen Hilfetext-Block unterhalb des Panel-Headers ein/aus.
 * Bei eingeklapptem Panel wird der Button automatisch ausgeblendet.
 *
 * Verwendung:
 * <p-panel [hisPanelHelptext]="'mein.hilfetext.key'" [hisPanelHelptextSubtitle]="'Mein Panel-Titel'">
 */
@Directive( {
    selector: 'p-panel[hisPanelHelptext]',
    standalone: false,
    host: {
        '(collapsedChange)': 'onCollapsedChange($event)'
    }
} )
export class HisPanelHelptextDirective implements AfterViewInit, OnDestroy {

    private elementRef = inject( ElementRef );
    private renderer = inject( Renderer2 );
    private hisUiService = inject( HisUiService );
    private viewContainerRef = inject( ViewContainerRef );
    private injector = inject( Injector );

    hisPanelHelptext = input<string>();
    hisPanelHelptextSubtitle = input<string>();

    private buttonComponentRef: ComponentRef<HisHelptextButtonComponent> | null = null;
    private contentComponentRef: ComponentRef<HisHelptextContentComponent> | null = null;
    private buttonSubscription: OutputRefSubscription | null = null;

    private readonly displayText = signal( false );
    /** Gibt an, ob das P-Panel eingeklappt ist. */
    private readonly isPanelCollapsed = signal( false );
    private readonly showContent = computed( () => this.displayText() && !this.isPanelCollapsed() );
    private readonly translatedText = computed( () => this.computeTranslatedText() );
    private readonly subtitle = computed( () => this.hisPanelHelptextSubtitle() ?? '' );

    /** Steuert Button-Sichtbarkeit, Hilfetext-Anzeige und DOM-Reinsertion des Inhalts abhängig vom Panel- und Anzeigestatus. */
    private readonly panelEffect = effect( () => {
        const collapsed = this.isPanelCollapsed();
        const show = this.showContent();

        if ( this.buttonComponentRef ) {
            this.renderer.setStyle( this.buttonComponentRef.location.nativeElement, 'display', collapsed ? 'none' : '' );
            this.buttonComponentRef.setInput( 'displayHelptext', show );
        }
        if ( this.contentComponentRef ) {
            this.contentComponentRef.setInput( 'displayHelptext', show );
        }

        if ( collapsed || !this.contentComponentRef ) {
            return;
        }

        const contentEl = this.contentComponentRef.location.nativeElement;
        // PrimeNG re-creates the .p-panel-content DOM node when the panel expands, which removes the
        // previously inserted content element from the DOM. afterNextRender ensures we wait for Angular's
        // next render cycle, when the DOM is fully rebuilt, before reinserting the content component.
        // We split into explicit earlyRead/write phases to avoid the default mixedReadWrite phase,
        // which Angular discourages due to potential layout-thrashing.
        afterNextRender( {
            earlyRead: () => {
                if ( !this.contentComponentRef ) {
                    return null;
                }
                const panelContent: HTMLElement | null = ( this.elementRef.nativeElement as HTMLElement ).querySelector( '.p-panel-content' );
                return panelContent && !panelContent.contains( contentEl ) ? panelContent : null;
            },
            write: ( panelContent ) => {
                if ( panelContent ) {
                    this.renderer.insertBefore( panelContent, contentEl, panelContent.firstChild );
                }
            }
        }, { injector: this.injector } );
    }
    );

    ngAfterViewInit(): void {
        const panelNative = this.elementRef.nativeElement as HTMLElement;
        const toggler: HTMLElement | null = panelNative.querySelector( '.p-panel-toggler' );
        const initiallyCollapsed = toggler?.getAttribute( 'aria-expanded' ) === 'false';

        if ( initiallyCollapsed ) {
            this.isPanelCollapsed.set( true );
            return;
        }

        this.initComponents();
    }

    private initComponents(): void {
        if ( !this.translatedText() ) {
            return;
        }

        const panelNative = this.elementRef.nativeElement as HTMLElement;
        const iconsContainer: HTMLElement | null = panelNative.querySelector( '.p-panel-icons' );
        if ( !iconsContainer ) {
            return;
        }

        this.createButtonComponent( iconsContainer );
        this.createContentComponent( panelNative );
    }

    ngOnDestroy(): void {
        this.buttonSubscription?.unsubscribe();
        this.buttonSubscription = null;
        this.buttonComponentRef?.destroy();
        this.buttonComponentRef = null;
        this.contentComponentRef?.destroy();
        this.contentComponentRef = null;
    }

    /** Wird ausgelöst, wenn sich der Zustand des Panels ändert (eingeklappt/ausgeklappt). */
    protected onCollapsedChange( isCollapsed: unknown ): void {
        this.isPanelCollapsed.set( !!isCollapsed );
        if ( isCollapsed ) {
            this.displayText.set( false );
        } else if ( !this.buttonComponentRef ) {
            this.initComponents();
        }
    }

    private createButtonComponent( iconsContainer: HTMLElement ): void {
        const helpTitle = this.hisUiService.translate( 'common.help' );
        const closingTitle = this.hisUiService.translate( 'common.helpClose' );
        this.buttonComponentRef = this.viewContainerRef.createComponent( HisHelptextButtonComponent );
        this.buttonComponentRef.setInput( 'title', helpTitle );
        this.buttonComponentRef.setInput( 'linkTitle', helpTitle );
        this.buttonComponentRef.setInput( 'closingTitle', closingTitle );
        this.buttonComponentRef.setInput( 'closingLinkTitle', closingTitle );
        this.buttonComponentRef.setInput( 'displayHelptext', false );
        this.renderer.insertBefore( iconsContainer, this.buttonComponentRef.location.nativeElement, iconsContainer.firstChild );
        this.buttonSubscription = this.buttonComponentRef.instance.displayHelptext.subscribe( show => this.displayText.set( show ) );
    }

    private createContentComponent( panelNative: HTMLElement ): void {
        this.contentComponentRef = this.viewContainerRef.createComponent( HisHelptextContentComponent );
        this.contentComponentRef.setInput( 'title', this.hisUiService.translate( 'common.help' ) );
        this.contentComponentRef.setInput( 'helptext', this.translatedText() );
        this.contentComponentRef.setInput( 'subtitle', this.subtitle() );
        this.contentComponentRef.setInput( 'displayHelptext', false );

        const panelContent: HTMLElement | null = panelNative.querySelector( '.p-panel-content' );
        if ( panelContent ) {
            this.renderer.insertBefore( panelContent, this.contentComponentRef.location.nativeElement, panelContent.firstChild );
        }
    }

    private computeTranslatedText(): string {
        const key = this.hisPanelHelptext();
        return key ? this.hisUiService.translate( key ) : '';
    }
}